import type { IPopupConfig } from "@similarweb/ui-components";
import type { PopupClickContainer } from "@similarweb/ui-components";
import useSetTimeout from "app/pages/sales-intelligence/hooks/useSetTimeout";
import { MediaQueryBreakpoint } from "app/pages/sales-intelligence/types/styles";
import type { PropsWithChildren, ReactElement, RefObject } from "react";
import { useEffect, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import { checkIsPopupBottomPlacement } from "./utils";

const OFFSET_BUFFER = 24;

type PopupContainerProps = Pick<PopupClickContainer, "reposition">;

type Props = {
    popupPlacement: IPopupConfig["placement"];
    triggerElementRef: RefObject<HTMLDivElement | null>;
    onVisibilityChange(isVisible: boolean): void;
} & Partial<PopupContainerProps>;

const ContentMediaQueryWrapper = ({
    children,
    popupPlacement,
    triggerElementRef,
    onVisibilityChange,
    ...popupContainerProps
}: PropsWithChildren<Props>): ReactElement => {
    const contentRef = useRef<HTMLDivElement>(null);

    const isMediumScreen = useMediaQuery({ screen: true, maxHeight: MediaQueryBreakpoint.MD });

    const setTimeout = useSetTimeout();

    useEffect(() => {
        const areElementsPresent = contentRef.current && triggerElementRef.current;

        const visibilityCheckConditions = [isMediumScreen, checkIsPopupBottomPlacement(popupPlacement)];

        if (areElementsPresent && visibilityCheckConditions.every(Boolean)) {
            const triggerRect = triggerElementRef.current.getBoundingClientRect();
            const nodeRect = contentRef.current.getBoundingClientRect();

            const minOffsetY = triggerRect.bottom + nodeRect.height + OFFSET_BUFFER;
            const viewPortHeight = window.innerHeight || document.documentElement.clientHeight;

            const isVisible = minOffsetY <= viewPortHeight;

            onVisibilityChange(isVisible);
            setTimeout(() => popupContainerProps.reposition?.());
        }
    }, [isMediumScreen, popupPlacement, triggerElementRef]);

    return <div ref={contentRef}>{children}</div>;
};

export default ContentMediaQueryWrapper;
